import React from 'react';
import { Box, Skeleton } from '@mui/material';

export interface ProductTypeTabItem {
  value: 'all' | 'camera' | 'lens' | 'accessory';
  label: string;
  /** Formatted count, when a real total is known for this tab. */
  count?: string;
}

export interface ProductTypeTabsProps {
  tabs: ProductTypeTabItem[];
  value: ProductTypeTabItem['value'];
  onChange: (value: ProductTypeTabItem['value']) => void;
  /** Pulse placeholders next to labels until per-tab totals are known. */
  countsLoading?: boolean;
  /**
   * Products the tabs cannot place (no product type), so the tab counts add up to
   * "All". Shown after the tabs as plain text, not a tab: there is nothing to filter to.
   */
  trailingNote?: string;
}

/**
 * Product type as tabs — the top-level split of the catalog, not a peer of
 * Brand. Underlined + count-highlighted when active.
 */
export default function ProductTypeTabs({ tabs, value, onChange, countsLoading = false, trailingNote }: ProductTypeTabsProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: '2px',
        flex: '1 1 auto',
        minWidth: 0,
        overflowX: 'auto',
        flexWrap: 'nowrap',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
      aria-busy={countsLoading}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            onClick={() => onChange(tab.value)}
            aria-pressed={active}
            sx={{
              appearance: 'none',
              background: 'none',
              border: 'none',
              borderBottom: '2px solid',
              borderBottomColor: active ? 'primary.main' : 'transparent',
              px: { xs: 1.25, sm: 1.75 },
              pt: 1.75,
              pb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.875,
              cursor: 'pointer',
              font: 'inherit',
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 500,
              color: active ? 'text.primary' : 'text.secondary',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <span>{tab.label}</span>
            {countsLoading ? (
              <Skeleton
                variant="rounded"
                animation="wave"
                width={28}
                height={14}
                aria-hidden
                data-testid="tab-count-skeleton"
              />
            ) : tab.count != null ? (
              <Box
                component="span"
                sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78125rem', color: active ? 'primary.main' : 'text.disabled' }}
              >
                {tab.count}
              </Box>
            ) : null}
          </Box>
        );
      })}
      {trailingNote && !countsLoading && (
        <Box
          component="span"
          sx={{ display: 'flex', alignItems: 'center', px: { xs: 1, sm: 1.25 }, flexShrink: 0, whiteSpace: 'nowrap', fontSize: '0.78125rem', color: 'text.disabled' }}
        >
          {trailingNote}
        </Box>
      )}
    </Box>
  );
}
